/**
 * ReferrerBasePage — manual-entry referrer database with RIS Kestral codes.
 * Each practitioner row auto-derives a RIS code (LASTNAME[:3] + FIRSTNAME[:3])
 * which the PM can override.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Building2, Plus, Search, Pencil, Trash2, Loader2, X, Save, Phone, Mail,
  AlertTriangle, Stethoscope, Sparkles,
} from 'lucide-react';
import Header from '../components/Header';
import BulkActions from '../components/BulkActions';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCENT = '#3fa758';
const DARK = '#1f4a2e';
const CATEGORIES = ['GP', 'Specialist', 'Allied Health', 'Hospital', 'Other'];

// Mirror of the backend's derive_ris_code() — keeps the live preview snappy
// without a network round-trip per keystroke.
function deriveRisCode(name) {
  if (!name) return '';
  const cleaned = name.replace(/\b(?:Dr|Prof|Mr|Mrs|Ms|Miss|A\/Prof|Assoc)\.?\b/gi, '');
  const parts = cleaned.split(/\s+/).map(p => p.replace(/[^A-Za-z]/g, '')).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 3).toUpperCase();
  const first = parts[0]; const last = parts[parts.length - 1];
  return (last.slice(0, 3) + first.slice(0, 3)).toUpperCase();
}

const empty = () => ({
  practice_name: '', category: 'GP',
  address: '', suburb: '', state: '', postcode: '',
  phone: '', fax: '', email: '', website: '',
  healthlink_edi: '', fax_only: false, notes: '',
  practitioners: [],
});

export default function ReferrerBasePage() {
  const { user } = useAuth();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total_practices: 0, by_category: {}, total_practitioners: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (filterCat) params.category = filterCat;
      const [r, s] = await Promise.all([
        axios.get(`${API}/referrers`, { params, withCredentials: true }),
        axios.get(`${API}/referrers/stats`, { withCredentials: true }),
      ]);
      setRows(r.data || []);
      setStats(s.data || { total_practices: 0 });
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filterCat]);
  const onSearch = (e) => { e.preventDefault(); refresh(); };

  return (
    <div className="min-h-screen bg-background" data-testid="referrer-base-page">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3" style={{ backgroundColor: `${ACCENT}1A`, color: ACCENT }}>
            <Building2 className="w-3.5 h-3.5" />
            <span className="text-[10px] font-bold uppercase tracking-wider">Referrer database · Kestral RIS-ready</span>
          </div>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ color: DARK }}>
            Referring <span style={{ color: ACCENT }}>practices</span>
          </h1>
          <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl">
            Add referrer clinics one at a time. Each entry can hold many practitioners — the RIS Kestral code auto-fills (3 letters of surname + 3 of first name, e.g. <strong>Linda Moss → MOSLIN</strong>) so the export is ready to upload to Kestral.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <Tile l="Practices" v={stats.total_practices} c={ACCENT} />
          <Tile l="Practitioners" v={stats.total_practitioners} c="#0EA5E9" />
          <Tile l="GPs" v={stats.by_category?.GP || 0} c="#16A34A" />
          <Tile l="Specialists" v={stats.by_category?.Specialist || 0} c="#7C3AED" />
        </div>

        <form onSubmit={onSearch} className="flex items-center gap-2 flex-wrap mb-4">
          <div className="flex items-center gap-2 bg-card border border-border rounded-xl px-3 py-2 flex-1 min-w-[240px]">
            <Search className="w-4 h-4 text-muted-foreground" />
            <input data-testid="referrer-search" value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search by practice, suburb, practitioner, RIS code…"
              className="flex-1 bg-transparent text-sm outline-none" />
          </div>
          <select data-testid="referrer-category-filter" value={filterCat} onChange={e => setFilterCat(e.target.value)}
            className="px-3 py-2 text-sm rounded-xl border border-border bg-card">
            <option value="">All categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button type="submit" className="text-xs font-semibold px-3 py-2 rounded-xl border border-border hover:bg-muted">Search</button>
          <BulkActions resource="referrers" accent={ACCENT} writable={isPM} onImported={refresh} />
          {isPM && (
            <button type="button" onClick={() => setEditing(empty())} data-testid="referrer-add"
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-xl text-white"
              style={{ backgroundColor: ACCENT }}>
              <Plus className="w-4 h-4" /> Add referrer
            </button>
          )}
        </form>

        {loading ? (
          <div className="bg-card border border-border rounded-2xl p-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="bg-card border border-border rounded-2xl p-10 text-center" data-testid="referrer-empty">
            <Building2 className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
            <h3 className="text-lg font-bold mb-1" style={{ color: DARK }}>No referrers yet</h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
              {isPM ? 'Click "Add referrer" to get started — start with the GPs and specialists who send you the most work.' : 'Ask a practice manager to add the first entry.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="referrer-list">
            {rows.map(r => <ReferrerCard key={r.referrer_id} r={r} isPM={isPM} onEdit={() => setEditing(r)} onDelete={() => setConfirmDel(r)} />)}
          </div>
        )}
      </div>

      {editing && <EditReferrerModal initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
      {confirmDel && <ConfirmDelete r={confirmDel} onClose={() => setConfirmDel(null)} onDeleted={() => { setConfirmDel(null); refresh(); }} />}
    </div>
  );
}

function Tile({ l, v, c }) {
  return (
    <div className="bg-card border border-border rounded-xl p-3.5">
      <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: c }}>{l}</div>
      <div className="text-2xl font-bold" style={{ color: DARK }}>{v}</div>
    </div>
  );
}

function ReferrerCard({ r, isPM, onEdit, onDelete }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-4 hover:border-current transition" data-testid={`referrer-card-${r.referrer_id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ backgroundColor: `${ACCENT}1A`, color: ACCENT }}>{r.category}</span>
          <h3 className="font-bold text-foreground mt-0.5">{r.practice_name}</h3>
          {(r.suburb || r.state) && <p className="text-xs text-muted-foreground">{[r.suburb, r.state, r.postcode].filter(Boolean).join(', ')}</p>}
        </div>
        {isPM && (
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={onEdit} className="p-1.5 rounded border border-border hover:bg-muted"><Pencil className="w-3.5 h-3.5" /></button>
            <button onClick={onDelete} className="p-1.5 rounded border border-red-500/30 text-red-500 hover:bg-red-500/5"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mb-2">
        {r.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{r.phone}</span>}
        {r.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{r.email}</span>}
      </div>
      {(() => {
        const namedPractitioners = (r.practitioners || []).filter(p => (p.name || '').trim());
        if (namedPractitioners.length === 0) return null;
        return (
          <div className="border-t border-border pt-2 text-xs space-y-0.5">
            <div className="font-semibold mb-0.5 text-muted-foreground inline-flex items-center gap-1"><Stethoscope className="w-3 h-3" /> {namedPractitioners.length} practitioner{namedPractitioners.length === 1 ? '' : 's'}</div>
            {namedPractitioners.slice(0, 3).map((p, i) => (
              <div key={i} className="text-muted-foreground flex items-baseline gap-2">
                <span>• {p.name}</span>
                {p.role && <span className="text-[10px]">— {p.role}</span>}
                {p.ris_code && <span className="font-mono text-[10px] px-1 py-0.5 rounded" style={{ backgroundColor: `${ACCENT}15`, color: ACCENT }}>{p.ris_code}</span>}
              </div>
            ))}
            {namedPractitioners.length > 3 && <div className="text-[10px] italic">+{namedPractitioners.length - 3} more</div>}
          </div>
        );
      })()}
    </div>
  );
}

function EditReferrerModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    ...initial,
    practitioners: (initial.practitioners || []).map(p => ({ ...p, _ris_manual: !!p.ris_code })),
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isNew = !initial.referrer_id;

  const addPractitioner = () => setForm(f => ({
    ...f,
    practitioners: [...(f.practitioners || []), { name: '', role: '', provider_number: '', ahpra_number: '', email: '', phone: '', ris_code: '', notes: '', _ris_manual: false }],
  }));

  const updPractitioner = (i, k, v) => setForm(f => {
    const arr = [...(f.practitioners || [])];
    const cur = { ...arr[i], [k]: v };
    // Auto-derive RIS code from name unless the user has typed in the RIS field manually
    if (k === 'name' && !cur._ris_manual) {
      cur.ris_code = deriveRisCode(v);
    }
    if (k === 'ris_code') {
      cur._ris_manual = true;
      cur.ris_code = (v || '').toUpperCase();
    }
    arr[i] = cur;
    return { ...f, practitioners: arr };
  });

  const resetRisCode = (i) => setForm(f => {
    const arr = [...(f.practitioners || [])];
    arr[i] = { ...arr[i], _ris_manual: false, ris_code: deriveRisCode(arr[i].name) };
    return { ...f, practitioners: arr };
  });

  const removePractitioner = (i) =>
    setForm(f => ({ ...f, practitioners: (f.practitioners || []).filter((_, idx) => idx !== i) }));

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const payload = {
        practice_name: form.practice_name, category: form.category,
        address: form.address || '', suburb: form.suburb || '', state: form.state || '',
        postcode: form.postcode || '', phone: form.phone || '', fax: form.fax || '',
        email: form.email || '', website: form.website || '',
        healthlink_edi: form.healthlink_edi || '', fax_only: !!form.fax_only,
        notes: form.notes || '',
        practitioners: (form.practitioners || []).map(({ _ris_manual, ...rest }) => rest),
      };
      if (isNew) await axios.post(`${API}/referrers`, payload, { withCredentials: true });
      else await axios.put(`${API}/referrers/${initial.referrer_id}`, payload, { withCredentials: true });
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  // Detect RIS-code collisions inside this referrer (different practitioners,
  // same code) and inside the same row (just visual cue — backend doesn't enforce).
  const risCollisions = useMemo(() => {
    const seen = {}; (form.practitioners || []).forEach((p, i) => { if (p.ris_code) (seen[p.ris_code] ||= []).push(i); });
    const dupes = new Set();
    Object.values(seen).forEach(idxs => { if (idxs.length > 1) idxs.forEach(i => dupes.add(i)); });
    return dupes;
  }, [form.practitioners]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="referrer-edit-modal">
      <div className="absolute inset-0 bg-black/50" onClick={busy ? undefined : onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto p-5 shadow-2xl space-y-4">
        <div className="flex items-start justify-between gap-3 pb-3 border-b border-border">
          <h3 className="font-['Outfit'] text-lg font-bold" style={{ color: DARK }}>{isNew ? 'Add referrer' : `Edit · ${initial.practice_name}`}</h3>
          <button onClick={onClose} disabled={busy}><X className="w-4 h-4" /></button>
        </div>
        {err && <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-2.5 text-xs text-red-600">{err}</div>}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field l="Practice name *"><input data-testid="referrer-name" value={form.practice_name} onChange={e => setF('practice_name', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Field>
          <Field l="Category *"><select data-testid="referrer-category" value={form.category} onChange={e => setF('category', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background">
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select></Field>
          <Field l="Phone"><input value={form.phone} onChange={e => setF('phone', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Field>
          <Field l="Email"><input type="email" value={form.email} onChange={e => setF('email', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Field>
          <Field l="Suburb"><input value={form.suburb} onChange={e => setF('suburb', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Field>
          <Field l="State"><input value={form.state} onChange={e => setF('state', e.target.value)} placeholder="NSW" className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Field>
          <Field l="Postcode"><input value={form.postcode} onChange={e => setF('postcode', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Field>
          <Field l="Fax"><input value={form.fax} onChange={e => setF('fax', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Field>
          <Field l="HealthLink EDI ID"><input value={form.healthlink_edi} onChange={e => setF('healthlink_edi', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Field>
          <Field l="Website"><input value={form.website} onChange={e => setF('website', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Field>
        </div>
        <Field l="Notes"><textarea value={form.notes} onChange={e => setF('notes', e.target.value)} rows={2} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Field>

        <div className="border-t border-border pt-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">Practitioners</h4>
            <button onClick={addPractitioner} data-testid="referrer-add-practitioner" className="text-xs font-semibold inline-flex items-center gap-1 px-2 py-1 rounded border border-border hover:bg-muted"><Plus className="w-3 h-3" /> Add practitioner</button>
          </div>
          {(form.practitioners || []).length === 0 ? (
            <p className="text-xs text-muted-foreground italic">No practitioners listed yet.</p>
          ) : (
            <div className="space-y-2">
              {form.practitioners.map((p, i) => (
                <div key={i} className="bg-muted/40 border border-border rounded-lg p-2.5">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
                    <input data-testid={`practitioner-name-${i}`} placeholder="Name" value={p.name} onChange={e => updPractitioner(i, 'name', e.target.value)} className="px-2 py-1.5 text-xs rounded border border-border bg-background" />
                    <input placeholder="Role (e.g. GP, Cardiologist)" value={p.role} onChange={e => updPractitioner(i, 'role', e.target.value)} className="px-2 py-1.5 text-xs rounded border border-border bg-background" />
                    <input placeholder="Provider number" value={p.provider_number} onChange={e => updPractitioner(i, 'provider_number', e.target.value)} className="px-2 py-1.5 text-xs rounded border border-border bg-background font-mono" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
                    <input placeholder="AHPRA #" value={p.ahpra_number} onChange={e => updPractitioner(i, 'ahpra_number', e.target.value)} className="px-2 py-1.5 text-xs rounded border border-border bg-background font-mono" />
                    <input placeholder="Email" value={p.email} onChange={e => updPractitioner(i, 'email', e.target.value)} className="px-2 py-1.5 text-xs rounded border border-border bg-background" />
                    <input placeholder="Phone" value={p.phone} onChange={e => updPractitioner(i, 'phone', e.target.value)} className="px-2 py-1.5 text-xs rounded border border-border bg-background" />
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <label className="absolute -top-1.5 left-2 px-1 text-[9px] font-bold uppercase tracking-wider bg-card text-muted-foreground">RIS Kestral code</label>
                      <input
                        data-testid={`practitioner-ris-${i}`}
                        placeholder="Auto: type Name first"
                        value={p.ris_code || ''}
                        maxLength={12}
                        onChange={e => updPractitioner(i, 'ris_code', e.target.value)}
                        className={`w-full px-2 py-1.5 text-xs font-mono uppercase rounded border-2 bg-background ${risCollisions.has(i) ? 'border-red-500/60' : ''} ${p._ris_manual && !risCollisions.has(i) ? 'ring-2 ring-sky-300/50' : ''}`}
                        style={!risCollisions.has(i) ? { borderColor: p._ris_manual ? '#0EA5E9' : ACCENT } : {}}
                      />
                    </div>
                    {p._ris_manual && p.name && (
                      <button onClick={() => resetRisCode(i)} title="Reset to auto-derived code" className="text-[10px] font-semibold px-2 py-1.5 rounded border border-border hover:bg-muted inline-flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> Auto
                      </button>
                    )}
                    <button onClick={() => removePractitioner(i)} className="p-1.5 rounded border border-red-500/30 text-red-500 hover:bg-red-500/5"><Trash2 className="w-3 h-3" /></button>
                  </div>
                  {risCollisions.has(i) && <p className="text-[10px] text-red-600 mt-1 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Duplicate RIS code on this referrer — give one practitioner a unique code (e.g. add a 4th letter).</p>}
                  {!p._ris_manual && p.ris_code && <p className="text-[10px] text-muted-foreground mt-1 inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> Auto-derived from name. Click the field to override.</p>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-border">
          <button onClick={onClose} disabled={busy} className="text-sm px-3 py-1.5 rounded-lg border border-border">Cancel</button>
          <button onClick={submit} disabled={busy || !form.practice_name} data-testid="referrer-save"
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-5 py-2 rounded-lg text-white disabled:opacity-50"
            style={{ backgroundColor: ACCENT }}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {isNew ? 'Add referrer' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ l, children }) {
  return <div><label className="block text-xs font-medium mb-1">{l}</label>{children}</div>;
}

function ConfirmDelete({ r, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await axios.delete(`${API}/referrers/${r.referrer_id}`, { withCredentials: true }); onDeleted(); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-md p-4 space-y-3">
        <h3 className="font-bold inline-flex items-center gap-2 text-red-600"><AlertTriangle className="w-4 h-4" /> Delete {r.practice_name}?</h3>
        <p className="text-xs text-muted-foreground">This will remove the practice and all its practitioners.</p>
        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <button onClick={onClose} disabled={busy} className="text-sm px-3 py-1.5 rounded-lg border border-border">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="referrer-delete-confirm" className="text-sm font-semibold px-4 py-1.5 rounded-lg bg-red-600 text-white disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}
